<template>
  <div>
    <div
      v-if="notificationsStore.loading"
      class="text-surface-500 text-center py-8"
    >
      <ProgressSpinner style="width: 2.5rem; height: 2.5rem" />
      <p class="mt-2">
        Loading…
      </p>
    </div>
    <div
      v-else-if="notificationsStore.error"
      class="text-red-600 dark:text-red-400 text-center py-6 px-2"
    >
      <p>{{ notificationsStore.error }}</p>
    </div>
    <div
      v-else-if="notificationsStore.list.length === 0"
      class="text-surface-500 text-center py-8"
    >
      <i class="pi pi-bell-slash text-4xl mb-2 block" />
      <p>No notifications yet</p>
    </div>
    <ul
      v-else
      class="list-none p-0 m-0 flex flex-col gap-0"
    >
      <li
        v-for="n in notificationsStore.list"
        :key="n.id"
        class="flex gap-3 p-3 rounded-lg cursor-pointer transition-colors border-b border-l-4 border-surface-200 last:border-b-0"
        :class="getNotificationTypeColors(n).row"
        @click="onItemClick(n)"
      >
        <div
          class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
          :class="getNotificationTypeColors(n).icon"
        >
          <i :class="['pi', getNotificationIcon(n.type)]" />
        </div>
        <div class="flex-1 min-w-0">
          <p class="font-medium text-surface-900 truncate">
            {{ n.title }}
          </p>
          <p class="text-sm text-surface-600 truncate">
            {{ n.subtitle }}
          </p>
          <p class="text-xs text-surface-500 mt-1">
            {{ formatNotificationTime(n.createdAt) }}
          </p>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { useRouter } from 'vue-router';
import { useNotificationsStore } from '@/stores/notificationsStore';

const props = defineProps({
  /** When true, navigate after clicking an item with a link (drawer mode). When false, page mode — same behaviour. */
  closeDrawer: {
    type: Function,
    default: null
  }
});

const router = useRouter();
const notificationsStore = useNotificationsStore();

const onItemClick = (notification) => {
  notificationsStore.markAsRead(notification.id);
  if (notification.link) {
    props.closeDrawer?.();
    router.push(notification.link);
  }
};

const getNotificationIcon = (type) => {
  const icons = {
    reminder: 'pi-bell',
    quote_acceptance: 'pi-file-edit',
    subhire_acceptance: 'pi-box',
    freelancer_shift_acceptance: 'pi-users',
    freelancer_shift_cancellation: 'pi-times-circle'
  };
  return icons[type] || 'pi-bell';
};

const getNotificationTypeColors = (n) => {
  const read = !!n.read;
  const red = {
    row: read ? 'border-l-red-400 bg-red-50/30 hover:bg-red-100/40' : 'border-l-red-500 bg-red-50 hover:bg-red-100',
    icon: read ? 'bg-red-100 text-red-600' : 'bg-red-200 text-red-800'
  };
  const amber = {
    row: read ? 'border-l-amber-400 bg-amber-50/30 hover:bg-amber-100/40' : 'border-l-amber-500 bg-amber-50 hover:bg-amber-100',
    icon: read ? 'bg-amber-100 text-amber-600' : 'bg-amber-200 text-amber-800'
  };
  const green = {
    row: read ? 'border-l-emerald-400 bg-emerald-50/30 hover:bg-emerald-100/40' : 'border-l-emerald-500 bg-emerald-50 hover:bg-emerald-100',
    icon: read ? 'bg-emerald-100 text-emerald-600' : 'bg-emerald-200 text-emerald-800'
  };
  const shiftStyle = n.type === 'freelancer_shift_acceptance'
    ? (n.variant === 'denied' ? red : n.variant === 'mixed' ? amber : green)
    : null;
  const orange = {
    row: read ? 'border-l-orange-400 bg-orange-50/30 hover:bg-orange-100/40' : 'border-l-orange-500 bg-orange-50 hover:bg-orange-100',
    icon: read ? 'bg-orange-100 text-orange-600' : 'bg-orange-200 text-orange-800'
  };
  const map = {
    reminder: orange,
    quote_acceptance: {
      row: read ? 'border-l-blue-400 bg-blue-50/30 hover:bg-blue-100/40' : 'border-l-blue-500 bg-blue-50 hover:bg-blue-100',
      icon: read ? 'bg-blue-100 text-blue-600' : 'bg-blue-200 text-blue-800'
    },
    subhire_acceptance: amber,
    freelancer_shift_acceptance: shiftStyle || green,
    freelancer_shift_cancellation: red
  };
  return map[n.type] || { row: read ? 'bg-surface-50 hover:bg-surface-100' : 'bg-surface-100 hover:bg-surface-200', icon: read ? 'bg-surface-200 text-surface-600' : 'bg-surface-300 text-surface-700' };
};

const formatNotificationTime = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
};
</script>
